import { ReactNode, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useProductAccess, MODULE_META } from '@/hooks/useProductAccess';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { Loader2 } from 'lucide-react';
import { FLUITY_LAST_PATH_KEY, isAppPath, getLastAppPath } from '@/lib/persistRoute';

function CrossDomainLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#2a20ad] to-[#655bf1]">
      <div className="flex flex-col items-center gap-4">
        <img src="/logos/logo-fluity-white.svg" alt="Fluity" className="h-10 w-auto object-contain" />
        <div className="h-7 w-7 rounded-full border-[3px] border-[#e0dbff] border-t-white animate-spin" />
        <p className="text-white/70 text-sm">Entrando no Fluity Chat...</p>
      </div>
    </div>
  )
}

interface ProductBasedRouteProps {
  children: ReactNode;
  requiredProduct?: string;
  adminOnly?: boolean;
}

export function ProductBasedRoute({
  children,
  adminOnly = false,
}: ProductBasedRouteProps) {
  const { hasAccess, isAdminMaster, isLoading: accessLoading, userProducts } = useProductAccess();
  const { user, loading: authLoading } = useAuth();
  const { loading: orgLoading } = useOrganizationContext();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoading = accessLoading || authLoading || orgLoading;
  const navigateRef = useRef(navigate);

  useEffect(() => {
    if (isAppPath(location.pathname)) {
      localStorage.setItem(FLUITY_LAST_PATH_KEY, location.pathname + (location.search || ''));
    }
  }, [location.pathname, location.search]);

  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  useEffect(() => {
    if (isLoading || !user) return;
    if (adminOnly && !isAdminMaster) {
      if (location.pathname !== '/chat') navigateRef.current('/chat', { replace: true });
    }
  }, [isAdminMaster, isLoading, location.pathname, adminOnly, user?.id]);

  const isPostCrossDomainLogin = sessionStorage.getItem('fluity-post-login') === 'cross_domain';

  if (isLoading) {
    if (isPostCrossDomainLogin) return <CrossDomainLoader />;
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    if (isPostCrossDomainLogin) return <CrossDomainLoader />;
    return null;
  }
  if (adminOnly && !isAdminMaster) return null;

  if (!hasAccess(location.pathname)) {
    const firstEnabledEntry = Object.entries(MODULE_META).find(([key]) => userProducts.includes(key));
    const fallbackPath = firstEnabledEntry ? firstEnabledEntry[1].defaultPath : '/chat';
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}
